import type { SyncedCookingInstruction } from '@/types/api';

interface CookingInstructionFormProps {
  formData: Partial<SyncedCookingInstruction>;
  onChange: (data: Partial<SyncedCookingInstruction>) => void;
}

export function CookingInstructionForm({ formData, onChange }: CookingInstructionFormProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
        <input
          type="text"
          value={formData.description || ''}
          onChange={(e) => onChange({ ...formData, description: e.target.value })}
          className="input w-full"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isGeneral"
            checked={formData.isGeneral ?? false}
            onChange={(e) => onChange({ ...formData, isGeneral: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isGeneral" className="ml-2 text-sm text-gray-700">General</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isFoodOnly"
            checked={formData.isFoodOnly ?? false}
            onChange={(e) => onChange({ ...formData, isFoodOnly: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isFoodOnly" className="ml-2 text-sm text-gray-700">Food Only</label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isDrinkOnly"
            checked={formData.isDrinkOnly ?? false}
            onChange={(e) => onChange({ ...formData, isDrinkOnly: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isDrinkOnly" className="ml-2 text-sm text-gray-700">Drink Only</label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
        <input
          type="number"
          value={formData.sortOrder ?? ''}
          onChange={(e) => onChange({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
          className="input w-full"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Button Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={formData.buttonColorCode != null ? '#' + (formData.buttonColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : '#ffffff'}
              onChange={(e) => onChange({ ...formData, buttonColorCode: parseInt(e.target.value.slice(1), 16) })}
              className="h-9 w-14 cursor-pointer rounded border border-gray-300 p-0.5"
            />
            <input
              type="text"
              value={formData.buttonColorCode != null ? '#' + (formData.buttonColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : ''}
              onChange={(e) => {
                const hex = e.target.value.replace('#', '');
                if (/^[0-9a-fA-F]{0,6}$/.test(hex)) {
                  onChange({ ...formData, buttonColorCode: hex.length === 6 ? parseInt(hex, 16) : undefined });
                }
              }}
              placeholder="#ffffff"
              className="input w-full"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={formData.textColorCode != null ? '#' + (formData.textColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : '#000000'}
              onChange={(e) => onChange({ ...formData, textColorCode: parseInt(e.target.value.slice(1), 16) })}
              className="h-9 w-14 cursor-pointer rounded border border-gray-300 p-0.5"
            />
            <input
              type="text"
              value={formData.textColorCode != null ? '#' + (formData.textColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : ''}
              onChange={(e) => {
                const hex = e.target.value.replace('#', '');
                if (/^[0-9a-fA-F]{0,6}$/.test(hex)) {
                  onChange({ ...formData, textColorCode: hex.length === 6 ? parseInt(hex, 16) : undefined });
                }
              }}
              placeholder="#000000"
              className="input w-full"
            />
          </div>
        </div>
      </div>
      {formData.buttonColorCode != null && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
          <div
            className="rounded px-4 py-2 text-center font-medium"
            style={{
              backgroundColor: '#' + ((formData.buttonColorCode ?? 0xffffff) >>> 0).toString(16).padStart(6, '0').slice(-6),
              color: '#' + ((formData.textColorCode ?? 0) >>> 0).toString(16).padStart(6, '0').slice(-6),
            }}
          >
            {formData.description || 'Cooking Instruction'}
          </div>
        </div>
      )}
    </div>
  );
}
